import type { MimirVideoItemRef } from '../../mimirItemRef';
import type { TimelineInternalOps } from '../../timelineInternalOps';
import type { InsertMainTrackObjectsAction } from '../timelineActions';
import type { VideoSourceRef } from '../../sourceRef';
import { addFractions, frac, subtractFractions } from '@mimir/common/util/integerMath';
import { UndoHelper } from '../../utils/undoHelper';
import { getUniqueId } from '../actionUtils';
import type { ModelAudioBox, ModelVideoBox } from '../../timelineModel';


export function insertMainTrackObjects(timelineInternalHelper: TimelineInternalOps, action: InsertMainTrackObjectsAction) {
  const undoHelper = new UndoHelper();

  const allSourceOffsets = timelineInternalHelper.getSourceStartOffsets();
  let durationOfInsertedItems = frac(0, 1);
  let insertionIndex = action.targetIndex;
  let sourceOffset = timelineInternalHelper.getMainTrackDurationInSeconds(insertionIndex);


  for (const item of action.videoItemsToInsert) {
    // First, we need to make sure we have a reference to this item,
    // adding one if needed
    const mimirItemRef: MimirVideoItemRef = {
      itemId: item.itemId,
      type: 'video',
      nAudioChannels: item.nAudioChannels,
      durationInSeconds: item.durationInSeconds,
    };
    undoHelper.add(timelineInternalHelper.addMimirItemRef(mimirItemRef));

    // Then, let's create a new source reference
    const sourceId = getUniqueId(undoHelper, timelineInternalHelper);

    const sourceRef: VideoSourceRef = {
      itemRef: mimirItemRef,
      srcId: sourceId,
      mediaStartOffset: subtractFractions(sourceOffset, item.inPointInSeconds),
    };

    const itemDuration = subtractFractions(item.outPointInSeconds, item.inPointInSeconds);
    sourceOffset = addFractions(sourceOffset, itemDuration);

    undoHelper.add(timelineInternalHelper.addSourceRef(sourceRef));

    const boxId = getUniqueId(undoHelper, timelineInternalHelper);
    const videoBox: ModelVideoBox = {
      type: 'video',
      sourceRef,
      boxId,
      inPointInSeconds: item.inPointInSeconds,
      outPointInSeconds: item.outPointInSeconds,
    };
    durationOfInsertedItems = addFractions(durationOfInsertedItems, itemDuration);

    undoHelper.add(timelineInternalHelper.insertVideoTrackBox(0, insertionIndex, videoBox));
    insertionIndex++;

    const nAudioBoxesToAdd = Math.min(item.nAudioChannels, timelineInternalHelper.getNumberOfAudioTracks());
    for (let channel = 0; channel < nAudioBoxesToAdd; channel++) {
      const audioBoxId = getUniqueId(undoHelper, timelineInternalHelper);
      const audioBox: ModelAudioBox = {
        type: 'audio',
        sourceRef,
        boxId: audioBoxId,
        inPointInSeconds: item.inPointInSeconds,
        outPointInSeconds: item.outPointInSeconds,
        channel,
      };
      undoHelper.add(timelineInternalHelper.addAudioTrackBox(channel, audioBox));
    }
  }

  // Once we have inserted all the items, we need to shift the start of the sources
  // on the right of the inserted items
  for (let i = insertionIndex; i < timelineInternalHelper.getMainTrackLength(); i++) {
    const box = timelineInternalHelper.getMainTrackBox(i);
    allSourceOffsets[box.sourceRef.srcId] = addFractions(allSourceOffsets[box.sourceRef.srcId], durationOfInsertedItems);
  }
  // Note that we set the start offsets of all sources (and not just the ones on the right of the inserted items)
  // to be sure to get back to the original state as it was before this action was executed
  Object.entries(allSourceOffsets).forEach(([srcId, offset]) => undoHelper.add(timelineInternalHelper.setSourceStartOffset(srcId, offset)));

  action.undo = () => undoHelper.undo();
}
